/**
 * Environment configuration module
 * Handles both build-time (Vite) and runtime (env.json) configuration
 */

export interface EnvConfig {
  ADMIN_API_URL: string;
  TURNSTILE_SITE_KEY: string;
  SUPPORT_CENTER_BUTTON_ENABLED: string;
  ENABLE_DEVELOPER_CONSOLE: string;
  ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES: string;
  APP_ENV: string;
  TENANT?: string;
  /** PostHog project API key (starts with phc_) */
  POSTHOG_API_KEY?: string;
  /** Self-hosted PostHog instance URL (e.g., https://posthog.internal.company.com) */
  POSTHOG_HOST?: string;
  /** Enable/disable PostHog tracking ('true' or 'false') */
  POSTHOG_ENABLED?: string;
}

// Build-time defaults from Vite
const buildTimeDefaults: EnvConfig = {
  ADMIN_API_URL: import.meta.env.VITE_ADMIN_API_URL || '',
  TURNSTILE_SITE_KEY: import.meta.env.VITE_TURNSTILE_SITE_KEY || '',
  SUPPORT_CENTER_BUTTON_ENABLED: import.meta.env.VITE_SUPPORT_CENTER_BUTTON_ENABLED || 'false',
  ENABLE_DEVELOPER_CONSOLE: import.meta.env.VITE_ENABLE_DEVELOPER_CONSOLE || 'false',
  ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES: import.meta.env.VITE_ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES || 'false',
  APP_ENV: import.meta.env.VITE_APP_ENV || 'dev',
  TENANT: import.meta.env.VITE_TENANT || 'utgl',
  POSTHOG_API_KEY: import.meta.env.VITE_POSTHOG_API_KEY || '',
  POSTHOG_HOST: import.meta.env.VITE_POSTHOG_HOST || '',
  POSTHOG_ENABLED: import.meta.env.VITE_POSTHOG_ENABLED || 'false'
};

// Runtime config state
let runtimeConfig: EnvConfig | null = null;
let configLoadPromise: Promise<EnvConfig> | null = null;

/**
 * Load runtime configuration from /env.json
 * Falls back to build-time defaults if not available
 */
export async function loadRuntimeConfig(): Promise<EnvConfig> {
  if (runtimeConfig) {
    return runtimeConfig;
  }

  if (configLoadPromise) {
    return configLoadPromise;
  }

  configLoadPromise = fetch('/env.json')
    .then((res) => {
      if (res.ok) {
        return res.json();
      }
      return null;
    })
    .catch(() => null)
    .then((runtimeEnv) => {
      // Merge: runtime config takes precedence over build-time defaults
      runtimeConfig = {
        ADMIN_API_URL: runtimeEnv?.ADMIN_API_URL || buildTimeDefaults.ADMIN_API_URL,
        TURNSTILE_SITE_KEY: runtimeEnv?.TURNSTILE_SITE_KEY || buildTimeDefaults.TURNSTILE_SITE_KEY,
        SUPPORT_CENTER_BUTTON_ENABLED: runtimeEnv?.SUPPORT_CENTER_BUTTON_ENABLED || buildTimeDefaults.SUPPORT_CENTER_BUTTON_ENABLED,
        ENABLE_DEVELOPER_CONSOLE: runtimeEnv?.ENABLE_DEVELOPER_CONSOLE || buildTimeDefaults.ENABLE_DEVELOPER_CONSOLE,
        ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES: runtimeEnv?.ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES || buildTimeDefaults.ENABLE_FEATURE_FLAG_OUTPUT_OVERRIDES,
        APP_ENV: runtimeEnv?.APP_ENV || buildTimeDefaults.APP_ENV,
        TENANT: runtimeEnv?.TENANT || buildTimeDefaults.TENANT,
        POSTHOG_API_KEY: runtimeEnv?.POSTHOG_API_KEY || buildTimeDefaults.POSTHOG_API_KEY,
        POSTHOG_HOST: runtimeEnv?.POSTHOG_HOST || buildTimeDefaults.POSTHOG_HOST,
        POSTHOG_ENABLED: runtimeEnv?.POSTHOG_ENABLED || buildTimeDefaults.POSTHOG_ENABLED
      };

      // Also set on window for legacy code that uses window.env
      (window as any).env = runtimeConfig;

      return runtimeConfig;
    });

  return configLoadPromise;
}

/**
 * Get current config (throws if not loaded)
 */
export function getConfig(): EnvConfig {
  if (!runtimeConfig) {
    // Return build-time defaults if runtime config hasn't loaded yet
    // This is safe for initial render
    return buildTimeDefaults;
  }
  return runtimeConfig;
}

/**
 * Check if runtime config has been loaded
 */
export function isConfigLoaded(): boolean {
  return runtimeConfig !== null;
}

/**
 * Get specific config value with type safety
 */
export function getEnv<K extends keyof EnvConfig>(key: K): EnvConfig[K] {
  return getConfig()[key];
}

/**
 * Validate required configuration
 * Returns array of missing required fields
 */
export function validateConfig(): string[] {
  const config = getConfig();
  const missing: string[] = [];

  if (!config.ADMIN_API_URL) {
    missing.push('ADMIN_API_URL');
  }

  return missing;
}
